import { useQuery } from '@tanstack/react-query';
import { useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, isBuiltinSectionType, SECTION_TYPES, sectionType, Thumb, useToast, type IconName } from '@/design-system';
import { sectionTypesQuery } from '@/features/library/api';
import { useStructureLabels } from './labels';
import { specOf, activate } from './PageStack';
import { blankSection, defaultInsertAt, insertSection, moveSection, primary, removeSection, type Sec, type StructureDoc } from './useStructure';
import s from './list.module.css';

/** The section types «Добавить секцию» offers: the server's (built-in and an Admin's custom ones), or the 12 built-in. */
export function useSectionTypeKeys(): { key: string; icon: IconName }[] {
  const types = useQuery(sectionTypesQuery).data;
  if (!types?.length) return SECTION_TYPES.map((x) => ({ key: x.key, icon: x.icon }));
  return types.map((x) => ({ key: x.key, icon: isBuiltinSectionType(x.key) ? sectionType(x.key).icon : 'shapes' }));
}

type Props = { doc: StructureDoc; selected: number; select: (i: number) => void };

/**
 * «СТРУКТУРА · перетащите»: the sections in order. HTML5 drag and drop reorders them; each row has a
 * mini wireframe of its design (or a dashed AI tile), its name, the design's name or «AI подберёт»,
 * and delete. «Добавить секцию» toggles the list of section types; a new one goes before the footer.
 */
export function Outline({ doc, selected, select }: Props) {
  const { t } = useTranslation('structure');
  const toast = useToast();
  const { section, designLabel } = useStructureLabels();
  const kinds = useSectionTypeKeys();
  const [drag, setDrag] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  const add = (key: string) => {
    const at = defaultInsertAt(doc.secs);
    doc.update((secs) => insertSection(secs, blankSection(key), at));
    select(at);
    setAdding(false);
    toast(t('toast.added', { section: section(key) }));
  };
  const remove = (i: number, x: Sec) => {
    doc.update((secs) => removeSection(secs, i));
    select(Math.max(0, Math.min(selected, doc.secs.length - 2)));
    toast(t('toast.deleted', { section: section(x.section_type) }));
  };
  const drop = (e: DragEvent, i: number) => {
    e.preventDefault();
    if (drag === null || drag === i) return setDrag(null);
    doc.update((secs) => moveSection(secs, drag, i));
    setDrag(null);
    select(i);
  };

  return (
    <div className={s.outline}>
      <div className={s.outlineHead}>
        <span className={s.caps}>{t('outline.title')}</span>
        <span className={s.hintSmall}>{t('outline.drag')}</span>
      </div>
      <div className={s.rows} role="list" aria-label={t('outline.label')}>
        {doc.secs.map((x, i) => {
          const p = primary(x);
          const name = section(x.section_type);
          const label = p ? (x.presets.length > 1 ? t('outline.extra', { label: designLabel(p), count: x.presets.length - 1 }) : designLabel(p)) : t('outline.ai');
          return (
            <div
              key={x.key}
              role="listitem"
              draggable
              className={s.row}
              aria-current={i === selected}
              data-dragging={drag === i}
              tabIndex={0}
              onClick={() => select(i)}
              onKeyDown={(e) => activate(e, () => select(i))}
              onDragStart={(e) => {
                // Firefox starts a drag only with data set.
                e.dataTransfer?.setData('text/plain', String(i));
                if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
                setDrag(i);
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => drop(e, i)}
              onDragEnd={() => setDrag(null)}
            >
              <Icon name="grip-vertical" size={13} className={s.grip} />
              <span className={s.mini} data-ai={!p}>
                {p ? (
                  <Thumb {...specOf(p)} className={s.fill} style={{ position: 'absolute' }} />
                ) : (
                  <span className={s.miniAi}>
                    <Icon name="sparkles" size={11} />
                  </span>
                )}
              </span>
              <span className={s.rowText}>
                <span className={s.rowName}>{name}</span>
                <span className={s.rowSub} data-ai={!p}>
                  {label}
                </span>
              </span>
              <button
                type="button"
                className={s.del}
                title={t('outline.delete')}
                aria-label={`${t('outline.delete')}: ${name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  remove(i, x);
                }}
              >
                <Icon name="trash-2" size={13} />
              </button>
            </div>
          );
        })}
      </div>
      {adding && (
        <div className={s.types} role="group" aria-label={t('outline.types_label')}>
          {kinds.map((k) => (
            <button key={k.key} type="button" className={s.type} onClick={() => add(k.key)}>
              <Icon name={k.icon} size={13} />
              <span className={s.ellipsis}>{section(k.key)}</span>
            </button>
          ))}
        </div>
      )}
      <button type="button" className={s.addBtn} aria-expanded={adding} onClick={() => setAdding((v) => !v)}>
        <Icon name={adding ? 'x' : 'plus'} size={14} />
        {adding ? t('outline.hide') : t('outline.add')}
      </button>
    </div>
  );
}
